import React, { useState } from 'react';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Star,
  Phone,
  Globe,
  Instagram,
  Navigation,
  Share2,
  CheckCircle2,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import {
  Lounge,
  LoungeSpecial,
  LoungeEvent,
  Badge,
  isOpenNow,
  AMENITY_ICONS,
} from '../data/lounges';

interface LoungeDetailViewProps {
  lounge: Lounge;
  userCheckInCount: number;
  earnedBadges: Badge[];
  onCheckIn: () => void;
  onBack: () => void;
  onNavigate: () => void;
  onShare: () => void;
  on7CLLearnMore?: () => void;
}

const LoungeDetailView: React.FC<LoungeDetailViewProps> = ({
  lounge,
  userCheckInCount,
  earnedBadges,
  onCheckIn,
  onBack,
  onNavigate,
  onShare,
  on7CLLearnMore,
}) => {
  const [showAllHours, setShowAllHours] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'specials' | 'events'>('about');
  const open = isOpenNow(lounge.hours);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  // Get active amenities
  const activeAmenities = Object.entries(lounge.amenities)
    .filter(([_, value]) => value === true)
    .map(([key]) => ({
      key,
      ...AMENITY_ICONS[key as keyof typeof AMENITY_ICONS],
    }));

  return (
    <div className="flex flex-col h-full bg-stone-900">
      {/* Header image / placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-stone-800 to-stone-900">
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-6xl opacity-30">🚬</span>
        </div>

        {/* Back button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 p-2 bg-black/50 backdrop-blur rounded-full"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        {/* Share button */}
        <button
          onClick={onShare}
          className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur rounded-full"
        >
          <Share2 className="w-5 h-5 text-white" />
        </button>

        {/* Featured badge */}
        {lounge.featured && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-stone-900 rounded-full text-sm font-medium">
            Featured Lounge
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto -mt-6">
        <div className="bg-stone-900 rounded-t-3xl">
          {/* Title section */}
          <div className="p-4 pb-0">
            <h1 className="text-2xl font-bold text-white">{lounge.name}</h1>

            <div className="flex items-center gap-3 mt-2">
              {/* Rating */}
              {lounge.rating && (
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-white font-medium">{lounge.rating}</span>
                  {lounge.reviewCount && (
                    <span className="text-stone-500 text-sm">
                      ({lounge.reviewCount} reviews)
                    </span>
                  )}
                </div>
              )}

              {/* Open status */}
              <div
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm ${
                  open
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-stone-800 text-stone-400'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    open ? 'bg-emerald-500' : 'bg-stone-500'
                  }`}
                />
                {open ? 'Open Now' : 'Closed'}
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-2 mt-3 text-stone-400">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">
                {lounge.address}, {lounge.city}, {lounge.state} {lounge.zip}
              </span>
            </div>

            {/* Quick actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={onCheckIn}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-stone-900 rounded-xl font-semibold"
              >
                <CheckCircle2 className="w-5 h-5" />
                Check In
                {userCheckInCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-stone-900/20 rounded text-xs">
                    {userCheckInCount}x
                  </span>
                )}
              </button>
              <button
                onClick={onNavigate}
                className="px-4 py-3 bg-stone-800 text-white rounded-xl"
              >
                <Navigation className="w-5 h-5" />
              </button>
              {lounge.phone && (
                <a
                  href={`tel:${lounge.phone}`}
                  className="px-4 py-3 bg-stone-800 text-white rounded-xl"
                >
                  <Phone className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* 7CL Promo Banner - Contextual placement */}
          {userCheckInCount >= 3 && on7CLLearnMore && (
            <div className="mx-4 mt-4 p-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">
                    Keep your clothes fresh after tonight's smoke
                  </p>
                  <p className="text-xs text-stone-400">
                    Discover 7CL - AI-powered smoke odor elimination
                  </p>
                </div>
                <button
                  onClick={on7CLLearnMore}
                  className="px-3 py-1.5 bg-amber-500 text-stone-900 rounded-lg text-sm font-medium"
                >
                  Learn More
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-stone-800 mt-4">
            {(['about', 'specials', 'events'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab ? 'text-amber-500' : 'text-stone-400'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'specials' && lounge.specials.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-500 rounded text-xs">
                    {lounge.specials.length}
                  </span>
                )}
                {tab === 'events' && lounge.events.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                    {lounge.events.length}
                  </span>
                )}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-4">
            {activeTab === 'about' && (
              <div className="space-y-6">
                {/* Description */}
                {lounge.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-2">
                      About
                    </h3>
                    <p className="text-stone-300 text-sm leading-relaxed">
                      {lounge.description}
                    </p>
                  </div>
                )}

                {/* Hours */}
                <div>
                  <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-2">
                    Hours
                  </h3>
                  <div className="bg-stone-800/50 rounded-xl p-3">
                    {(showAllHours ? lounge.hours : lounge.hours.filter(h => h.day === today)).map(
                      (hours) => (
                        <div
                          key={hours.day}
                          className={`flex justify-between py-1.5 ${
                            hours.day === today ? 'text-white' : 'text-stone-400'
                          }`}
                        >
                          <span className="text-sm">
                            {hours.day}
                            {hours.day === today && (
                              <span className="ml-2 text-xs text-amber-500">Today</span>
                            )}
                          </span>
                          <span className="text-sm">
                            {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                          </span>
                        </div>
                      )
                    )}
                    <button
                      onClick={() => setShowAllHours(!showAllHours)}
                      className="flex items-center gap-1 text-amber-500 text-sm mt-2"
                    >
                      {showAllHours ? (
                        <>
                          Show less <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          Show all hours <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-2">
                    Amenities
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {activeAmenities.map((amenity) => (
                      <div
                        key={amenity.key}
                        className="flex items-center gap-2 p-2 bg-stone-800/50 rounded-lg"
                      >
                        <span className="text-lg">{amenity.icon}</span>
                        <span className="text-sm text-stone-300">{amenity.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact & Links */}
                <div>
                  <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-2">
                    Contact
                  </h3>
                  <div className="space-y-2">
                    {lounge.phone && (
                      <a
                        href={`tel:${lounge.phone}`}
                        className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-lg text-stone-300 hover:bg-stone-800"
                      >
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">{lounge.phone}</span>
                      </a>
                    )}
                    {lounge.website && (
                      <a
                        href={lounge.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-lg text-stone-300 hover:bg-stone-800"
                      >
                        <Globe className="w-4 h-4" />
                        <span className="text-sm flex-1">Website</span>
                        <ExternalLink className="w-4 h-4 text-stone-500" />
                      </a>
                    )}
                    {lounge.instagram && (
                      <a
                        href={`https://instagram.com/${lounge.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-lg text-stone-300 hover:bg-stone-800"
                      >
                        <Instagram className="w-4 h-4" />
                        <span className="text-sm flex-1">@{lounge.instagram}</span>
                        <ExternalLink className="w-4 h-4 text-stone-500" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specials' && (
              <div className="space-y-3">
                {lounge.specials.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No current specials</p>
                  </div>
                ) : (
                  lounge.specials.map((special) => (
                    <SpecialCard key={special.id} special={special} />
                  ))
                )}
              </div>
            )}

            {activeTab === 'events' && (
              <div className="space-y-3">
                {lounge.events.length === 0 ? (
                  <div className="text-center py-8 text-stone-500">
                    <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No upcoming events</p>
                  </div>
                ) : (
                  lounge.events.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Your visits section */}
          {userCheckInCount > 0 && (
            <div className="p-4 border-t border-stone-800">
              <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-3">
                Your Visits
              </h3>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-500">{userCheckInCount}</div>
                  <div className="text-xs text-stone-500">Check-ins</div>
                </div>
                {earnedBadges.length > 0 && (
                  <div className="flex -space-x-2">
                    {earnedBadges.slice(0, 5).map((badge) => (
                      <div
                        key={badge.id}
                        className="w-8 h-8 bg-stone-800 rounded-full flex items-center justify-center text-lg border-2 border-stone-900"
                        title={badge.name}
                      >
                        {badge.icon}
                      </div>
                    ))}
                    {earnedBadges.length > 5 && (
                      <div className="w-8 h-8 bg-stone-800 rounded-full flex items-center justify-center text-xs text-stone-400 border-2 border-stone-900">
                        +{earnedBadges.length - 5}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Special card component
const SpecialCard: React.FC<{ special: LoungeSpecial }> = ({ special }) => {
  const typeColors = {
    cigar: 'bg-amber-500/20 text-amber-500',
    drink: 'bg-purple-500/20 text-purple-400',
    event: 'bg-blue-500/20 text-blue-400',
    combo: 'bg-emerald-500/20 text-emerald-400',
  };

  return (
    <div className="p-4 bg-stone-800/50 rounded-xl">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-white">{special.title}</h4>
            <span className={`px-2 py-0.5 rounded text-xs ${typeColors[special.type]}`}>
              {special.type}
            </span>
          </div>
          <p className="text-sm text-stone-400">{special.description}</p>
          {special.validDays && special.validDays.length > 0 && (
            <p className="text-xs text-stone-500 mt-2">
              Valid: {special.validDays.join(', ')}
            </p>
          )}
        </div>
        {special.discount && (
          <div className="px-3 py-1.5 bg-amber-500 text-stone-900 rounded-lg text-sm font-bold">
            {special.discount}
          </div>
        )}
      </div>
    </div>
  );
};

// Event card component
const EventCard: React.FC<{ event: LoungeEvent }> = ({ event }) => {
  const typeLabels = {
    tasting: 'Tasting',
    brand_rep: 'Brand Rep Visit',
    live_music: 'Live Music',
    pairing: 'Pairing Event',
    members_night: 'Members Night',
    other: 'Event',
  };

  const eventDate = new Date(event.date);
  const isUpcoming = eventDate >= new Date();

  return (
    <div className={`p-4 bg-stone-800/50 rounded-xl ${!isUpcoming ? 'opacity-50' : ''}`}>
      <div className="flex gap-3">
        {/* Date box */}
        <div className="w-14 h-14 bg-blue-500/20 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
          <span className="text-xs text-blue-400 uppercase">
            {eventDate.toLocaleDateString('en-US', { month: 'short' })}
          </span>
          <span className="text-xl font-bold text-white">
            {eventDate.getDate()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white truncate">{event.title}</h4>
          <p className="text-sm text-stone-400 mt-0.5">{event.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {event.time}
            </span>
            <span className="px-2 py-0.5 bg-stone-700 rounded">
              {typeLabels[event.type]}
            </span>
            {event.rsvpRequired && (
              <span className="text-amber-500">RSVP Required</span>
            )}
          </div>
          {event.brandSponsor && (
            <p className="text-xs text-amber-500 mt-1">
              Featuring: {event.brandSponsor}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoungeDetailView;
